/**
 * Get environment variable from multiple possible sources in order of precedence:
 *
 * 1. Window.__env[key] (runtime config object, common in containerized deployments)
 * 2. Window[key] (direct window property, can be injected via script tag)
 * 3. Undefined (var not found)
 *
 * This function supports both flat injection styles and nested config objects, making it compatible with most
 * deployment patterns.
 */
function getEnvironmentVariable(key: string): unknown {
  // Check window.__env first (containerized/Docker pattern)
  const windowAsAny = window as unknown as Record<string, unknown>;
  const windowEnv = windowAsAny['__env'];
  if (windowEnv && typeof windowEnv === 'object' && key in (windowEnv as Record<string, unknown>)) {
    return (windowEnv as Record<string, unknown>)[key];
  }

  // Fall back to direct window properties
  return windowAsAny[key];
}

/**
 * Resolve the shell application URL used by remotes when they must hand off authentication to the shell.
 *
 * Priority:
 *
 * 1. AUTH_SHELL_APP_URL environment variable
 * 2. Local dev port heuristic (:4201/:4202 -> :4200)
 * 3. Current origin
 */
export function getShellAppUrl(): string {
  const configured = getEnvironmentVariable('AUTH_SHELL_APP_URL');
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim().replace(/\/$/, '');
  }

  const origin = window.location.origin;
  if (origin.endsWith(':4201') || origin.endsWith(':4202')) {
    return origin.replace(/:420[12]$/, ':4200');
  }

  return origin;
}

/** Build a shell redirect URL carrying the current URL as returnUrl. */
export function buildShellAuthenticationRedirectUrl(
  shellAppUrl: string,
  currentUrl: string,
  returnPath?: string
): string {
  const query = new URLSearchParams({ returnUrl: currentUrl });
  if (returnPath && returnPath.startsWith('/')) {
    query.set('returnPath', returnPath);
  }
  return `${shellAppUrl}/?${query.toString()}`;
}
